/**
 * Project Scope Definitions
 *
 * Defines the available project scopes used to filter which
 * boilerplate sections are included in construction documents.
 */

/**
 * Project scope options for UI selection
 * Each scope can trigger inclusion of specific boilerplate sections
 */
export const PROJECT_SCOPES = [
  {
    id: 'kitchen',
    label: 'Kitchen Remodel',
    description: 'Kitchen renovation or upgrade',
    icon: 'kitchen',
    triggers: ['electrical', 'plumbing', 'mechanical'], // Implicitly includes these scopes
  },
  {
    id: 'bathroom',
    label: 'Bathroom Remodel',
    description: 'Bathroom renovation or upgrade',
    icon: 'bathroom',
    triggers: ['electrical', 'plumbing', 'mechanical'],
  },
  {
    id: 'addition',
    label: 'Addition',
    description: 'Room addition or expansion',
    icon: 'add_home',
    triggers: ['structural', 'electrical', 'plumbing', 'mechanical'],
  },
  {
    id: 'full-remodel',
    label: 'Full Remodel',
    description: 'Comprehensive renovation',
    icon: 'construction',
    triggers: [
      'structural',
      'electrical',
      'plumbing',
      'mechanical',
      'demolition',
    ],
  },
  {
    id: 'electrical',
    label: 'Electrical Work',
    description: 'Electrical system changes',
    icon: 'electrical_services',
    triggers: [],
  },
  {
    id: 'plumbing',
    label: 'Plumbing Work',
    description: 'Plumbing system changes',
    icon: 'plumbing',
    triggers: [],
  },
  {
    id: 'mechanical',
    label: 'Mechanical/HVAC',
    description: 'HVAC system changes',
    icon: 'hvac',
    triggers: [],
  },
  {
    id: 'hvac',
    label: 'HVAC Only',
    description: 'Heating/cooling system only',
    icon: 'air',
    triggers: ['mechanical'],
  },
  {
    id: 'structural',
    label: 'Structural Work',
    description: 'Structural modifications',
    icon: 'foundation',
    triggers: [],
  },
  {
    id: 'demolition',
    label: 'Demolition',
    description: 'Demolition of existing elements',
    icon: 'delete_forever',
    triggers: [],
  },
  {
    id: 'accessibility',
    label: 'Accessibility',
    description: 'ADA/accessibility improvements',
    icon: 'accessible',
    triggers: [],
  },
];

/**
 * Expand scopes to include triggered scopes
 * e.g., ['kitchen'] becomes ['kitchen', 'electrical', 'plumbing', 'mechanical']
 *
 * @param {Array<string>} selectedScopes - User-selected scopes
 * @returns {Array<string>} Expanded scopes including triggers
 */
export const expandScopes = (selectedScopes) => {
  const expanded = new Set(selectedScopes);

  selectedScopes.forEach((scopeId) => {
    const scope = PROJECT_SCOPES.find((s) => s.id === scopeId);
    if (scope && scope.triggers) {
      scope.triggers.forEach((trigger) => expanded.add(trigger));
    }
  });

  return Array.from(expanded);
};

/**
 * Get scope by ID
 * @param {string} scopeId - Scope identifier
 * @returns {Object|null} Scope definition or null
 */
export const getScopeById = (scopeId) => {
  return PROJECT_SCOPES.find((s) => s.id === scopeId) || null;
};

/**
 * Get common scope combinations for quick selection
 */
export const COMMON_SCOPE_PRESETS = [
  {
    id: 'kitchen-remodel',
    label: 'Kitchen Remodel',
    scopes: ['kitchen'],
  },
  {
    id: 'bathroom-remodel',
    label: 'Bathroom Remodel',
    scopes: ['bathroom'],
  },
  {
    id: 'kitchen-bath',
    label: 'Kitchen & Bath',
    scopes: ['kitchen', 'bathroom'],
  },
  {
    id: 'room-addition',
    label: 'Room Addition',
    scopes: ['addition'],
  },
  {
    id: 'whole-house',
    label: 'Whole House Remodel',
    scopes: ['full-remodel'],
  },
  {
    id: 'adu',
    label: 'ADU / Guest House',
    scopes: ['addition', 'full-remodel'],
  },
];

export default {
  PROJECT_SCOPES,
  expandScopes,
  getScopeById,
  COMMON_SCOPE_PRESETS,
};
